import { forwardRef } from 'react';

import Box, { BoxProps } from '@mui/material/Box';
import Link from '@mui/material/Link';
import { useTheme } from '@mui/material/styles';

import { RouterLink } from 'src/routes/components';

// ----------------------------------------------------------------------

export interface LogoProps extends BoxProps {
  disabledLink?: boolean;
  color?: string;
  width?: number;
  height?: number;
}

const Logo = forwardRef<HTMLDivElement, LogoProps>(
  ({ disabledLink = false, color, width, height, sx, ...other }, ref) => {
    const theme = useTheme();

    const PRIMARY_LIGHT = theme.palette.primary.light;

    const PRIMARY_MAIN = theme.palette.primary.main;

    const PRIMARY_DARK = theme.palette.primary.dark;

    // OR using local (public folder)
    // -------------------------------------------------------
    // const logo = (
    //   <Box
    //     component="img"
    //     src="/logo/logo_single.svg" => your path
    //     sx={{ width: 40, height: 40, cursor: 'pointer', ...sx }}
    //   />
    // );

    const logo = (
      <Box
        ref={ref}
        component="div"
        sx={{
          width: width || 56,
          height: height || 55,
          display: 'inline-flex',
          ...sx,
        }}
        {...other}
      >
        <svg
          width={width || '56'}
          height={height || '55'}
          viewBox="0 0 56 55"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M27.2481 15.6825C27.2481 16.2884 27.149 16.8942 26.942 17.4679C26.7782 17.9314 26.541 18.4317 26.2004 18.9136C24.9243 20.7357 23.3161 22.1814 21.2423 23.0993C20.656 23.3563 20.0481 23.5491 19.4358 23.7051C18.6857 23.8933 17.9225 24.008 17.1551 24.086L14.3872 24.3706C16.5343 24.3844 18.6986 24.6414 20.7508 25.4445C22.1736 25.9999 23.4368 26.7801 24.5104 27.8128C25.571 26.8903 26.4031 25.7199 26.9679 24.2788C27.2826 23.471 27.468 22.6266 27.6663 21.7867C27.6793 21.727 27.6965 21.6214 27.7181 21.6168C27.8215 21.5985 27.8215 21.7086 27.8388 21.7821C28.0457 22.4935 28.2182 23.2186 28.4596 23.9162C29.0158 25.5088 29.8608 26.7985 30.9775 27.7944C32.0252 26.8123 33.2927 26.0412 34.7586 25.4629C36.259 24.8709 37.8111 24.4257 39.3804 24.3431H41.0188C38.7811 24.3293 36.5392 24.0218 34.418 23.1406C33.8316 22.8973 32.9004 22.4659 31.8441 21.6444C30.542 20.6393 29.628 19.4827 29.0072 18.4776C28.7614 18.0829 28.5674 17.6561 28.4294 17.2063C28.2742 16.7106 28.2052 16.1966 28.2182 15.6825V15.0997C28.2397 14.3011 28.4553 13.5071 28.839 12.8049L28.852 12.7819C29.2874 11.9833 29.8824 11.1022 30.6929 10.2439C31.8743 8.99095 33.0383 8.30711 33.5859 7.99961C34.8621 7.28823 36.2547 6.97614 37.6774 6.72371C39.8547 6.3336 49.5252 6.36573 49.5252 6.36573V20.318H49.6114C52.8751 20.2997 55.5094 17.4771 55.5094 14.0028V0H39.389C37.8197 0.0826122 36.2676 0.527787 34.7672 1.11984C33.3013 1.69813 32.0295 2.46918 30.9861 3.45135C29.3995 4.92919 28.3217 6.88434 27.8215 9.39483C27.8 9.4958 27.8431 9.62432 27.731 9.69316C27.7051 9.57384 27.6836 9.45451 27.662 9.33518C27.4335 8.16943 27.0886 7.04498 26.5065 6.03068C25.9461 5.04852 25.2778 4.19486 24.5147 3.46971C23.4411 2.43706 22.1822 1.65683 20.7551 1.10149C18.6986 0.298319 16.5386 0.0458946 14.3915 0.0275363H0V13.9798C0 17.4633 2.65582 20.2905 5.93249 20.2905H6.1222V6.21886C6.1222 6.21886 9.59289 6.37032 14.1544 6.42539C16.655 6.45293 19.1728 6.71912 21.501 7.84357C22.9798 8.55495 23.9326 9.41779 24.2043 9.66103C25.3597 10.712 26.153 11.8594 26.6876 12.8416C26.6876 12.8462 26.6963 12.8554 26.6963 12.86C27.067 13.5484 27.2481 14.3194 27.2524 15.0997V15.6825H27.2481Z"
            fill={color || '#428263'}
          />
          <path
            d="M34.7634 28.8644C31.0685 30.3239 28.6498 32.972 27.8134 37.1394C27.7918 37.2403 27.835 37.3688 27.7229 37.4377C27.697 37.3184 27.6754 37.199 27.6539 37.0797C27.4254 35.914 27.0762 34.7895 26.4984 33.7752C25.1403 31.407 23.1873 29.7961 20.7513 28.846C18.6948 28.0428 16.5348 27.818 14.3834 27.8042L0.431641 27.7812V27.8042C0.431641 31.2923 3.09178 34.124 6.37277 34.1194C11.7448 34.1103 9.58477 34.1194 14.1462 34.1699C16.6469 34.1975 19.1647 34.4636 21.4929 35.5881C22.9717 36.2995 23.9245 37.1623 24.1961 37.4056C25.3516 38.4566 26.1449 39.604 26.6795 40.5861C27.1408 41.4352 27.3693 42.4036 27.3607 43.3858H28.1497C28.1411 42.3853 28.3739 41.3893 28.8482 40.5265C29.2836 39.7279 29.8786 38.8467 30.6848 37.9884C31.8661 36.7355 33.0302 36.0516 33.5821 35.7441C34.8583 35.0328 36.2508 34.7207 37.6736 34.4682C39.8509 34.0781 42.0281 34.1148 44.214 34.1148H49.2972C50.9312 34.1148 52.4186 33.4081 53.4879 32.2653C54.5614 31.127 55.2254 29.5482 55.2254 27.8042L39.3809 27.9511C37.8116 28.0337 36.2595 28.2677 34.7591 28.8644H34.7634Z"
            fill={color || '#428263'}
          />
          <path
            d="M34.7632 39.7687C31.0683 41.2282 28.6496 43.8764 27.8132 48.0437C27.7917 48.1447 27.8348 48.2732 27.7227 48.342C27.6968 48.2227 27.6752 48.1033 27.6537 47.984C27.4252 46.8183 27.076 45.6938 26.4982 44.6795C25.1401 42.3113 23.1871 40.7004 20.7511 39.7503C18.6946 38.9472 16.5346 38.7223 14.3832 38.7085L8.0454 38.6855L6.39844 38.7085C6.39844 42.1966 9.05858 45.0788 12.3396 45.0742H14.146C16.6467 45.1018 19.1645 45.368 21.4927 46.4924C22.9715 47.2038 23.9243 48.0666 24.1959 48.3099C25.3514 49.3609 26.1447 50.5083 26.6793 51.4904C27.1406 52.3395 27.3691 53.3079 27.3605 54.2901H28.1495C28.1409 53.2895 28.3737 52.2936 28.848 51.4308C29.2834 50.6322 29.8784 49.751 30.6846 48.8928C31.8659 47.6398 33.03 46.9559 33.5819 46.6484C34.8581 45.9371 36.2507 45.625 37.6734 45.3725C39.4971 45.0467 43.856 45.0192 43.856 45.0192C45.49 45.0192 46.9774 44.3124 48.0467 43.1696C49.1202 42.0314 49.7842 40.4525 49.7842 38.7085L39.3807 38.8554C37.8114 38.938 36.2593 39.172 34.7589 39.7687H34.7632Z"
            fill={color || '#428263'}
          />
        </svg>
      </Box>
    );

    if (disabledLink) {
      return logo;
    }

    return (
      <Link component={RouterLink} href="/" sx={{ display: 'contents' }}>
        {logo}
      </Link>
    );
  }
);

export default Logo;
